import { useState } from "react";
import { Link } from "react-router-dom";
import { GlossPacksSection } from "@/marketing/GlossPacksSection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// -------- Sample glossaries (same 5 industries the extension bundles) --------
const DEMOS = {
  nursing: {
    label: "Nursing / Medical",
    story: 'The patient in 304 is <mark>NPO</mark> until 0600 with a <mark>STAT</mark> order pending <mark>ABG</mark> results. The <mark>RN</mark> checks the <mark>MAR</mark> before the next <mark>ADL</mark> pass, charts <mark>PRN</mark> pain in the <mark>EHR</mark>, and reviews the <mark>H&P</mark> from admission. <mark>BP</mark> is trending up, <mark>SpO2</mark> holding at 94% on 2L via <mark>NC</mark>, and the <mark>CBC</mark> shows mild leukocytosis. <mark>Dx</mark>: rule-out sepsis. Hand-off to <mark>ICU</mark> at 1900; make sure the <mark>CNA</mark> knows about the <mark>DNR</mark> and that we\'re still <mark>HIPAA</mark>-compliant with the family updates.',
    terms: { NPO: "Nothing by mouth", STAT: "Immediately, urgent", ABG: "Arterial Blood Gas", RN: "Registered Nurse", MAR: "Medication Administration Record", ADL: "Activities of Daily Living", PRN: "Pro re nata — as needed", EHR: "Electronic Health Record", "H&P": "History and Physical", BP: "Blood Pressure", SpO2: "Peripheral oxygen saturation", NC: "Nasal Cannula", CBC: "Complete Blood Count", Dx: "Diagnosis", ICU: "Intensive Care Unit", CNA: "Certified Nursing Assistant", DNR: "Do Not Resuscitate", HIPAA: "Health Insurance Portability and Accountability Act" },
  },
  legal: {
    label: "Legal / Paralegal",
    story: 'Plaintiff filed a <mark>MTC</mark> after our <mark>FRCP 26</mark> disclosures were served. Opposing counsel demands all <mark>ESI</mark> and pushed for a <mark>Rule 30(b)(6)</mark> deposition. Recommend a <mark>PO</mark> covering <mark>ACP</mark> and <mark>WP</mark> comms before we produce anything. The <mark>SOL</mark> runs in 14 months and the <mark>TRO</mark> expires next week. Our client wants to explore <mark>ADR</mark> — I\'ve scheduled the <mark>JAMS</mark> mediation for the 14th. If that fails, expect a <mark>MSJ</mark> filed by December and a full <mark>Voir Dire</mark> by spring. Also, get the executed <mark>NDA</mark> from the <mark>SME</mark> before any prep call.',
    terms: { MTC: "Motion to Compel", "FRCP 26": "Federal Rule of Civil Procedure 26", ESI: "Electronically Stored Information", "Rule 30(b)(6)": "Corporate representative deposition rule", PO: "Protective Order", ACP: "Attorney-Client Privilege", WP: "Work Product doctrine", SOL: "Statute of Limitations", TRO: "Temporary Restraining Order", ADR: "Alternative Dispute Resolution", JAMS: "Judicial Arbitration and Mediation Services", MSJ: "Motion for Summary Judgment", "Voir Dire": "Jury selection questioning", NDA: "Non-Disclosure Agreement", SME: "Subject Matter Expert" },
  },
  realestate: {
    label: "Real Estate",
    story: 'The buyer submitted an <mark>ACO</mark> with a clean <mark>CMA</mark> comping the neighborhood. Our <mark>LTV</mark> is 72%, <mark>PMI</mark> waived, <mark>APR</mark> locked at 6.875% with a 45-day <mark>FICO</mark>-driven rate hold. Seller credited $4,000 at closing on the <mark>HUD-1</mark>, the <mark>CO</mark> arrived from the city, and the <mark>EMD</mark> wire clears tomorrow. <mark>HOA</mark> dues are current, <mark>MLS</mark> status flipped to pending, and the <mark>DOM</mark> was only 9 days. The <mark>TC</mark> is finalizing the <mark>DA</mark>, and we\'re scheduling the final <mark>walk-thru</mark> Thursday before <mark>COE</mark>.',
    terms: { ACO: "All Cash Offer", CMA: "Comparative Market Analysis", LTV: "Loan-to-Value ratio", PMI: "Private Mortgage Insurance", APR: "Annual Percentage Rate", FICO: "Fair Isaac Corporation credit score", "HUD-1": "HUD-1 Settlement Statement", CO: "Certificate of Occupancy", EMD: "Earnest Money Deposit", HOA: "Homeowners Association", MLS: "Multiple Listing Service", DOM: "Days on Market", TC: "Transaction Coordinator", DA: "Disclosure Agreement", "walk-thru": "Final walk-through inspection", COE: "Close of Escrow" },
  },
  tech: {
    label: "Software / Tech",
    story: 'We\'re shipping the new <mark>API</mark> behind a feature flag in <mark>k8s</mark>. <mark>SSO</mark> is blocked on <mark>IdP</mark> config — the <mark>SAML</mark> assertion is missing a claim. <mark>CI/CD</mark> is green on main, <mark>PR</mark> #4127 fixes the <mark>CSRF</mark> regression and adds proper <mark>CORS</mark> headers, and <mark>UAT</mark> starts Monday for the <mark>MVP</mark>. We still need to wire the <mark>CDN</mark> to the new <mark>S3</mark> origin and finish the <mark>SDK</mark> docs before <mark>GA</mark>. <mark>SLA</mark> is 99.9%, <mark>SLO</mark> targets are set in the <mark>OKR</mark> doc, and the <mark>on-call</mark> rotation covers weekends via <mark>PagerDuty</mark>.',
    terms: { API: "Application Programming Interface", k8s: "Kubernetes (container orchestration)", SSO: "Single Sign-On", IdP: "Identity Provider", SAML: "Security Assertion Markup Language", "CI/CD": "Continuous Integration / Continuous Deployment", PR: "Pull Request", CSRF: "Cross-Site Request Forgery", CORS: "Cross-Origin Resource Sharing", UAT: "User Acceptance Testing", MVP: "Minimum Viable Product", CDN: "Content Delivery Network", S3: "Amazon Simple Storage Service", SDK: "Software Development Kit", GA: "General Availability release", SLA: "Service Level Agreement", SLO: "Service Level Objective", OKR: "Objectives and Key Results", "on-call": "Rotating engineer available for incidents", PagerDuty: "Incident alerting platform" },
  },
  restaurant: {
    label: "Restaurant / Kitchen",
    story: '<mark>BOH</mark>, fire two on the pass. <mark>86</mark> the soft-shell and the <mark>catch</mark> of the day — we\'re out. <mark>AD</mark> on table 12: three steaks, one <mark>MR</mark>, two <mark>WD</mark>, one <mark>MW</mark> with the sauce <mark>OTS</mark>. <mark>VIP</mark> at booth 4 wants <mark>OTH</mark> apps from the <mark>GM</mark> — comp the round. <mark>FOH</mark>, reset the <mark>POS</mark> and re-fire the <mark>2-top</mark>\'s appetizer. Server 3 is in the <mark>weeds</mark>; someone <mark>run food</mark> to 22. Also, the <mark>MOD</mark> needs the <mark>comp</mark> log signed before end of shift, and prep the <mark>mise</mark> for brunch.',
    terms: { BOH: "Back of House (kitchen)", FOH: "Front of House", "86": "Eighty-six — out of stock / cancel", catch: "Fresh fish special", AD: "All Day (total count for a table)", MR: "Medium Rare", WD: "Well Done", MW: "Medium Well", OTS: "On the Side", VIP: "Very Important Person", OTH: "On the House (comped)", GM: "General Manager", POS: "Point of Sale system", "2-top": "Table for two", weeds: "Overwhelmed / falling behind", "run food": "Deliver a plate to a table", MOD: "Manager on Duty", comp: "Complimentary (free) item", mise: "Mise en place — prep station setup" },
  },
};

// -------- Direct-download URL for the built extension zip --------
// Chrome Web Store listing URL — update this after CWS approval
const CWS_LISTING_URL = "https://chromewebstore.google.com/detail/glosslink/pjfeijggadjjnciaoppapehckfjeogod";

// Phones/tablets (incl. iPads in desktop mode, which report as Mac with touch)
const IS_MOBILE_DEVICE =
  /iPad|iPhone|Android|Mobi/i.test(navigator.userAgent) ||
  (navigator.maxTouchPoints > 1 && /Mac/i.test(navigator.platform));

// Yellow highlighted term with hover-definition tooltip (mimics the extension)
const HoverTerm = ({ def, children }) => (
  <span className="relative group inline-block">
    <mark className="bg-yellow-200 border-b-2 border-yellow-500 rounded-sm px-1 font-semibold cursor-help" data-testid="hover-term">{children}</mark>
    <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 whitespace-nowrap bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-20" data-testid="hover-term-tooltip">{def}</span>
  </span>
);

// Full-page mobile advertisement (phones/tablets can't run Chrome extensions)
const MobileLanding = () => {
  const [copied, setCopied] = useState(false);
  const copyLink = async () => {
    fetch(`${BACKEND_URL}/api/track/mobile-copy`, { method: "POST" }).catch(() => {});
    try {
      await navigator.clipboard.writeText("https://glosslink.app");
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) { /* clipboard unavailable */ }
  };
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans flex flex-col" data-testid="mobile-landing">
      <div className="bg-slate-900 text-white text-center text-sm px-4 py-3 leading-snug">
        💻 <span className="font-semibold">GlossLink is a desktop Chrome extension</span> — it can't install on phones or tablets.
      </div>
      <main className="flex-1 px-6 py-10 text-center max-w-md mx-auto">
        <div className="flex items-center justify-center gap-2 font-extrabold text-2xl mb-6"><span className="text-3xl">📒</span> GlossLink</div>
        <h1 className="text-3xl font-extrabold tracking-tight leading-tight">
          Onboard employees <span className="text-blue-600">faster</span>. Make online classes <span className="text-blue-600">easier</span>.
        </h1>
        <p className="mt-4 text-lg font-bold text-slate-800 leading-snug">
          Every abbreviation. Every acronym. Jargon. Complicated words. <span className="text-blue-600">Defined the instant you see them.</span>
        </p>
        <p className="mt-5 text-base text-slate-700">
          Double-click a word, type what it means once — GlossLink highlights it with <b>your</b> definition on every website and PDF you'll ever open. <b>Never Google it again.</b>
        </p>
        <p className="mt-6 text-base text-slate-700 leading-relaxed bg-white border border-slate-200 rounded-2xl p-5 text-left">
          To a lawyer, <mark className="bg-yellow-200 border-b-2 border-yellow-500 rounded-sm px-1 font-semibold">PO</mark> = <i>Protective Order</i>. To a nurse, <mark className="bg-yellow-200 border-b-2 border-yellow-500 rounded-sm px-1 font-semibold">PO</mark> = <i>Post-Op</i>. To a manager, <mark className="bg-yellow-200 border-b-2 border-yellow-500 rounded-sm px-1 font-semibold">PO</mark> = <i>Purchase Order</i>. GlossLink highlights them all — with <b>your</b> meanings.
        </p>
        <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-5">
          <div className="font-bold text-lg">Free — your first 20 words, forever</div>
          <div className="text-sm text-slate-600 mt-1">20-day free trial of everything (starts with your first word) · then 20 words free forever · $9.99/mo or $75/yr for unlimited · Cancel anytime</div>
        </div>
        <div className="mt-8">
          <div className="font-semibold text-slate-800 mb-3">On your computer later? Grab the link now:</div>
          <button onClick={copyLink} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-xl text-lg shadow-lg shadow-blue-600/20 transition" data-testid="mobile-copy-link-btn">
            {copied ? "✓ Link copied!" : "Copy link for my computer"}
          </button>
          <p className="mt-3 text-xs text-slate-500">Paste it into Chrome on your Windows, Mac, or Chromebook → click "Add to Chrome" → done.</p>
        </div>
      </main>
      <footer className="text-center text-xs text-slate-400 py-6">© {new Date().getFullYear()} GlossLink · glosslink.app</footer>
    </div>
  );
};

const Home = () => {
  const [activeIndustry, setActiveIndustry] = useState("nursing");
  const [showHighlights, setShowHighlights] = useState(true);
  const demo = DEMOS[activeIndustry];

  if (IS_MOBILE_DEVICE) return <MobileLanding />;

  return (
    <div className="min-h-screen overflow-x-hidden bg-slate-50 text-slate-900 font-sans">
      {/* -------- NAV -------- */}
      <nav className="sticky top-0 z-50 bg-white/85 backdrop-blur border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-2 font-extrabold text-2xl tracking-tight" data-testid="nav-brand">
            <span className="text-2xl">📒</span> GlossLink
          </a>
          <div className="hidden sm:flex items-center gap-6 text-sm">
            <a href="#features" className="hover:text-blue-600" data-testid="nav-features">Features</a>
            <a href="#demo" className="hover:text-blue-600" data-testid="nav-demo">Live demo</a>
            <a href="#teams" className="hover:text-blue-600" data-testid="nav-teams">For teams</a>
            <a href="#learners" className="hover:text-blue-600" data-testid="nav-learners">For learners</a>
            <Link to="/packs" className="hover:text-blue-600 font-semibold text-blue-700" data-testid="nav-packs">Gloss Packs</Link>
            <Link to="/for-nurses" className="hover:text-blue-600" data-testid="nav-nurses">For nurses</Link>
            <a href="#pricing" className="hover:text-blue-600" data-testid="nav-pricing">Pricing</a>
            <a href="#faq" className="hover:text-blue-600" data-testid="nav-faq">FAQ</a>
            <a href={CWS_LISTING_URL} target="_blank" rel="noopener noreferrer" className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold" data-testid="nav-install">Install</a>
          </div>
        </div>
      </nav>

      {/* -------- HERO -------- */}
      <header id="top" className="max-w-6xl mx-auto px-6 pt-16 pb-16 text-center">
        <div className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full mb-6">Chrome extension · 20-day free trial of everything · 20 words free forever · Gloss Packs included</div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight max-w-4xl mx-auto leading-tight">
          Read anything. <span className="text-blue-600">Understand every term.</span>
        </h1>
        <p className="mt-4 text-xl sm:text-2xl font-bold text-slate-800 max-w-3xl mx-auto leading-snug">
          GlossLink highlights the words of <span className="text-blue-600">your</span> world on every page and PDF — and defines them on the spot.
        </p>
        <p className="mt-5 text-lg sm:text-xl font-semibold text-slate-700 max-w-3xl mx-auto leading-snug">
          Every abbreviation. Every acronym. Jargon and numbers too. Complicated words. <span className="text-blue-600">Defined the instant you see them.</span>
        </p>
        <p className="mt-4 text-base sm:text-lg text-slate-600 max-w-2xl mx-auto leading-snug">
          <b>New — Gloss Packs:</b> nursing students get their whole textbook glossary built in with one click (6,000+ terms, word for word). Everyone else builds a glossary as they read, or shares one with their team.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a href={CWS_LISTING_URL} target="_blank" rel="noopener noreferrer" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-lg shadow-blue-600/20 transition" data-testid="hero-install-btn">
            Add to Chrome — free →
          </a>
          <Link to="/packs" className="bg-white border-2 border-blue-600 text-blue-700 hover:bg-blue-50 px-8 py-4 rounded-xl font-bold text-lg transition" data-testid="hero-packs-btn">
            Browse Gloss Packs
          </Link>
        </div>
        <div className="mt-6 text-xs text-slate-500">Built for nursing students · online classes · onboarding new employees · ESL learners · anyone drowning in jargon</div>
      </header>

      {/* -------- GLOSS PACKS (featured) -------- */}
      <GlossPacksSection />

      {/* -------- DEMO -------- */}
      <section id="demo" className="max-w-4xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold text-center mb-3">See GlossLink in action</h2>
        <p className="text-center text-slate-600 mb-4 max-w-2xl mx-auto">Pick your field — <b>nursing, legal, real estate, software, restaurant</b> — and read a realistic snippet the way you'd see it at work.</p>
        <p className="text-center text-2xl sm:text-3xl font-extrabold text-blue-600 mb-8" data-testid="demo-hover-callout">👀 Now hover any <HoverTerm def="Like this! Hover any yellow term and your definition pops up instantly.">yellow term</HoverTerm> ↓</p>

        {/* Before / After toggle */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <button
            onClick={() => setShowHighlights(false)}
            className={`px-8 py-3 rounded-xl font-bold text-base transition-all ${
              !showHighlights
                ? "bg-slate-900 text-white shadow-lg scale-105"
                : "bg-white text-slate-600 border-2 border-slate-200 hover:border-slate-400"
            }`}
            data-testid="demo-toggle-before"
          >
            👀 Before
          </button>
          <span className="text-slate-400 text-sm font-medium">vs</span>
          <button
            onClick={() => setShowHighlights(true)}
            className={`px-8 py-3 rounded-xl font-bold text-base transition-all ${
              showHighlights
                ? "bg-blue-600 text-white shadow-lg scale-105"
                : "bg-white text-slate-600 border-2 border-slate-200 hover:border-blue-400"
            }`}
            data-testid="demo-toggle-after"
          >
            ✨ After (with GlossLink)
          </button>
        </div>
        <p className="text-center text-xs text-slate-500 -mt-4 mb-8 max-w-md mx-auto">
          {showHighlights
            ? "This is what a page looks like with GlossLink turned on. Hover any yellow term."
            : "This is how a new hire sees it — no highlights, no definitions, just raw jargon."}
        </p>
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {Object.entries(DEMOS).map(([key, d]) => (
            <button
              key={key}
              onClick={() => setActiveIndustry(key)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition ${activeIndustry === key ? "bg-blue-600 text-white" : "bg-white border border-slate-300 text-slate-700 hover:border-blue-400"}`}
              data-testid={`demo-tab-${key}`}
            >
              {d.label}
            </button>
          ))}
        </div>
        <article className={`border rounded-2xl p-8 transition-all ${showHighlights ? "bg-white border-slate-200" : "bg-slate-50 border-slate-300"}`} data-testid="demo-story">
          <p
            className={`text-lg leading-relaxed ${showHighlights ? "" : "text-slate-700"}`}
            dangerouslySetInnerHTML={{
              __html: showHighlights
                ? demo.story.replace(/<mark>(.+?)<\/mark>/g, (_, t) => `<mark class="bg-yellow-200 border-b-2 border-yellow-500 px-1 rounded-sm cursor-help font-semibold" title="${(demo.terms[t] || "").replace(/"/g, "&quot;")}">${t}</mark>`)
                : demo.story.replace(/<\/?mark>/g, ""),
            }}
          />
        </article>
      </section>

      {/* -------- SAME LETTERS, DIFFERENT WORLDS -------- */}
      <section className="bg-white border-y border-slate-200" data-testid="same-letters-section">
        <div className="max-w-3xl mx-auto px-6 py-14 text-center">
          <h2 className="text-base md:text-lg font-bold text-blue-600 uppercase tracking-wide mb-5">Same letters. Different worlds.</h2>
          <p className="text-lg text-slate-700 leading-relaxed">
            The same abbreviation means completely different things in different jobs. To a lawyer, <HoverTerm def="Protective Order">PO</HoverTerm> = <i>Protective Order</i>. To a nurse, <HoverTerm def="Post-Op">PO</HoverTerm> = <i>Post-Op</i>. To a manager, <HoverTerm def="Purchase Order">PO</HoverTerm> = <i>Purchase Order</i>. To an ESL learner, <HoverTerm def="Promoting health or well-being; morally good">wholesome</HoverTerm> is a word you look up 4 times a week — until GlossLink remembers it for you.
          </p>
          <p className="mt-4 text-sm text-slate-500">👆 Hover the yellow words — that's exactly how GlossLink feels on every page.</p>
        </div>
      </section>

      {/* -------- FOR ONBOARDING & TEAMS -------- */}
      <section id="teams" className="bg-slate-900 text-white py-20" data-testid="teams-section">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="text-sm font-bold text-amber-400 uppercase tracking-wider mb-3">🏢 For onboarding & teams</div>
            <h2 className="text-3xl sm:text-4xl font-extrabold max-w-3xl mx-auto leading-tight">New hires speak your company's language on <span className="text-amber-400">day one</span>.</h2>
            <p className="mt-4 text-slate-300 max-w-2xl mx-auto">Every company runs on its own acronyms, systems, and jargon — and every new employee spends weeks decoding them. Give them your glossary instead.</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-6 mb-12">
            {[
              { n: "1", title: "Build your master glossary once", body: "HR or a team lead lists your company's terms — acronyms, tools, internal lingo — with the official definitions." },
              { n: "2", title: "Share one link", body: "Upload it at glosslink.app/share and get a single link. Drop it in your onboarding doc, welcome email, or Slack." },
              { n: "3", title: "Every worker clicks once", body: "Every company term now highlights with the official definition on everything they read — intranet, docs, tickets, PDFs." },
            ].map((s, i) => (
              <div key={i} className="bg-slate-800 border border-slate-700 rounded-2xl p-6" data-testid={`teams-step-${i + 1}`}>
                <div className="w-9 h-9 rounded-full bg-amber-400 text-slate-900 font-extrabold flex items-center justify-center mb-4">{s.n}</div>
                <h3 className="font-bold text-lg mb-2">{s.title}</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 text-center">
            <p className="text-lg font-bold">Team seats — one invoice, no employee credit cards.</p>
            <p className="mt-3 text-3xl font-extrabold text-amber-400" data-testid="teams-price">$65<span className="text-base font-semibold text-slate-300"> per user / year · 10-seat minimum</span></p>
            <p className="mt-2 text-sm text-slate-300 max-w-xl mx-auto">Every seat gets full Pro — unlimited words, PDFs, quizzes, share links. Fewer "what does X stand for?" questions, faster ramp-up, smoother onboarding.</p>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="mailto:support@glosslink.app?subject=GlossLink%20Team%20Seats" className="bg-amber-400 hover:bg-amber-300 text-slate-900 px-8 py-3.5 rounded-xl font-bold transition" data-testid="teams-contact-btn">Get seats for your team →</a>
              <Link to="/share" className="text-amber-400 hover:text-amber-300 underline font-semibold" data-testid="teams-share-link">Try the glossary share tool</Link>
            </div>
          </div>
        </div>
      </section>

      {/* -------- HOW IT WORKS -------- */}
      <section className="bg-white border-y border-slate-200 py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-3">Three steps. Done in a minute.</h2>
          <p className="text-center text-slate-600 mb-12">No accounts. No cloud. Your glossary lives on your computer.</p>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { n: "1", title: "Add words as you read", body: "Double-click any word on any page, type what it means, done — it's saved and highlighted instantly. Or paste a list, upload a CSV, or ask ChatGPT/Claude with our built-in prompt." },
              { n: "2", title: "Browse like normal", body: "GlossLink watches every page and PDF you open and quietly highlights matches in gold." },
              { n: "3", title: "Hover for the definition", body: "That's it. No tabs, no searches, no context-switching. Your knowledge — always one hover away." },
            ].map((s, i) => (
              <div key={i} className="relative bg-slate-50 border border-slate-200 rounded-2xl p-6" data-testid={`step-card-${i}`}>
                <div className="absolute -top-4 left-6 w-10 h-10 rounded-full bg-blue-600 text-white font-bold text-lg flex items-center justify-center shadow-lg shadow-blue-600/30">{s.n}</div>
                <h3 className="font-bold text-lg mb-2 mt-3">{s.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* -------- FEATURES -------- */}
      <section id="features" className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold text-center mb-3">Why people keep it installed</h2>
        <p className="text-center text-slate-600 mb-14 max-w-xl mx-auto">Built for people who read a lot of jargon-heavy text and don't have time to look up every term.</p>
        <div className="grid sm:grid-cols-3 gap-6">
          {[
            { icon: "🎯", title: "Anything you want highlighted", body: "Acronyms, abbreviations, jargon, medical terms, product names, even full phrases — if it's in your glossary, it lights up on the page like your own personal hover dictionary." },
            { icon: "🤖", title: "AI builds your glossary for you", body: "One built-in prompt gets ChatGPT, Perplexity, or Claude to draft a full glossary for your field in seconds." },
            { icon: "🔒", title: "100% private", body: "Everything runs locally in Chrome. Your terms and the pages you read never touch a server. No tracking, no analytics." },
          ].map((f, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-lg hover:-translate-y-0.5 transition" data-testid={`feature-card-${i}`}>
              <div className="text-4xl mb-4">{f.icon}</div>
              <h3 className="font-bold text-lg mb-2">{f.title}</h3>
              <p className="text-slate-600 text-sm leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* -------- LEARNERS (dedicated section for language learners, students, curious readers) -------- */}
      <LearnersSection />

      {/* -------- PRICING -------- */}
      <section id="pricing" className="max-w-5xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold text-center mb-3">Simple pricing</h2>
        <p className="text-center text-slate-600 mb-12">Try everything free for 20 days · then 20 words free forever · Gloss Packs included at no extra charge · Cancel anytime.</p>
        <div className="grid sm:grid-cols-3 gap-6">
          <div className="bg-white border border-slate-200 rounded-2xl p-8" data-testid="pricing-free">
            <div className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Free</div>
            <div className="mt-2 text-5xl font-extrabold">$0<span className="text-lg font-normal text-slate-500">/forever</span></div>
            <ul className="mt-6 space-y-2 text-sm text-slate-700">
              <li>✓ Your first 20 words — highlighted forever</li>
              <li>✓ Double-click any word → add your definition</li>
              <li>✓ Hover definitions on every site</li>
              <li>✓ 20-day free trial of everything — starts when you add your first word or Gloss Pack</li>
              <li>✓ No card required</li>
            </ul>
            <a href={CWS_LISTING_URL} target="_blank" rel="noopener noreferrer" className="mt-8 block text-center bg-white border-2 border-slate-900 hover:bg-slate-50 text-slate-900 py-3 rounded-lg font-semibold" data-testid="pricing-free-cta">Add to Chrome free</a>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-8" data-testid="pricing-monthly">
            <div className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Monthly</div>
            <div className="mt-2 text-5xl font-extrabold">$9.99<span className="text-lg font-normal text-slate-500">/mo</span></div>
            <ul className="mt-6 space-y-2 text-sm text-slate-700">
              <li>✓ <b>Unlimited</b> words & glossaries</li>
              <li>✓ CSV upload, AI import, bulk paste</li>
              <li>✓ PDF highlighting</li>
              <li>✓ Quiz mode — flashcards, multiple choice, type the term</li>
              <li>✓ Team share links</li>
              <li>✓ Works with English + all major languages</li>
              <li>✓ Cancel anytime</li>
            </ul>
            <a href={CWS_LISTING_URL} target="_blank" rel="noopener noreferrer" className="mt-8 block text-center bg-slate-900 hover:bg-slate-800 text-white py-3 rounded-lg font-semibold" data-testid="pricing-monthly-cta">Start free trial</a>
          </div>
          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white rounded-2xl p-8 shadow-xl shadow-blue-600/20 relative" data-testid="pricing-yearly">
            <div className="absolute top-4 right-4 bg-yellow-400 text-slate-900 text-xs font-bold px-2 py-1 rounded-full">SAVE 37%</div>
            <div className="text-sm font-semibold text-blue-100 uppercase tracking-wide">Yearly</div>
            <div className="mt-2 text-5xl font-extrabold">$75<span className="text-lg font-normal text-blue-200">/yr</span></div>
            <ul className="mt-6 space-y-2 text-sm text-blue-50">
              <li>✓ Everything in Monthly</li>
              <li>✓ Click-to-add, quizzes & every feature included</li>
              <li>✓ Save 37% vs monthly</li>
              <li>✓ Priority support</li>
              <li>✓ Cancel anytime</li>
            </ul>
            <a href={CWS_LISTING_URL} target="_blank" rel="noopener noreferrer" className="mt-8 block text-center bg-white text-blue-700 hover:bg-blue-50 py-3 rounded-lg font-bold" data-testid="pricing-yearly-cta">Start free trial</a>
          </div>
        </div>
        <p className="mt-8 text-center text-sm text-slate-500" data-testid="pricing-terms">
          💻 Desktop Chrome only · 20 words free forever · 20-day free trial of Pro — try everything before you pay · All sales final; cancel anytime to stop future charges
        </p>
      </section>

      {/* -------- FAQ -------- */}
      <section id="faq" className="max-w-3xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold text-center mb-10">Quick answers</h2>
        <div className="space-y-4">
          {[
            { q: "What can GlossLink highlight?", a: "Anything you put in your glossary — acronyms (NPO, API, LTV), abbreviations (Dr., MR, WD), jargon phrases (Motion to Compel, All Cash Offer), product names, medical terms, legal terms, restaurant lingo… whatever you read a lot of." },
            { q: "Is GlossLink a hover dictionary or word highlighter?", a: "Yes — GlossLink is a custom glossary Chrome extension: it highlights words from your list on any webpage or PDF and shows your definition when you hover. Think of it as a hover dictionary, acronym expander, and vocabulary builder in one — for medical abbreviations, legal terms, ESL vocabulary, or your company's internal acronyms." },
            { q: "Does it work on PDFs?", a: "Yes. GlossLink highlights matching terms inside PDFs that open in Chrome, in addition to regular web pages." },
            { q: "Where does my glossary live?", a: "In your browser, using chrome.storage.local. It never leaves your computer. We do not run a server that sees your data." },
            { q: "What are Gloss Packs?", a: "Ready-made glossaries copied word for word from open textbooks (starting with the full Open RN nursing line). Open glosslink.app/packs, pick your textbook, click Add to my GlossLink, and every term in it is highlighted on everything you read with the book's exact definition and source. Packs are included with GlossLink — your 20-day free trial unlocks whole packs, and after that 20 of each pack's most-used terms stay free forever." },
            { q: "How does the free plan and trial work?", a: "Install GlossLink and add your first word or Gloss Pack — that starts a 20-day free trial of everything: unlimited words, every Gloss Pack, Auto-detect, PDFs, share links. No card needed. After 20 days you keep 20 words free forever (your 20 most-used pack terms stay defined); the rest of your glossary shows in grey so you can see what Pro would unlock. Pro is $9.99/mo or $75/year. Quizzes and flashcards are free for everyone, always." },
            { q: "How do I add, change, or delete a word?", a: "Add or change: double-click any word on any page — a '+ Add to GlossLink' button appears. Click it, type (or edit) the definition, save. It highlights instantly, everywhere. Delete or manage your whole list: click the GlossLink icon in your Chrome toolbar and open the 'Add, edit & delete terms' section." },
            { q: "Do I have to build the glossary myself?", a: "No. Double-click any word on any page and type its meaning — done. Or use the built-in prompt that asks ChatGPT / Claude / Perplexity to generate a starter glossary for your field. Copy → paste → done." },
            { q: "Does it work on phones or tablets?", a: "No — GlossLink is a desktop Chrome extension. Chrome doesn't support extensions on phones or tablets (that's true for every extension, including Grammarly). It works on Windows, Mac, Linux, and Chromebooks." },
            { q: "Do you offer refunds?", a: "GlossLink gives you 20 words free forever, and the 20-day free trial lets you test every paid feature before paying a cent. Because of that, all sales are final. You can cancel anytime and you won't be charged again." },
          ].map((f, i) => (
            <details key={i} className="group bg-white border border-slate-200 rounded-xl p-5 open:shadow-md transition" data-testid={`faq-item-${i}`}>
              <summary className="cursor-pointer font-semibold list-none flex items-center justify-between">
                <span>{f.q}</span>
                <span className="text-blue-600 text-xl transition group-open:rotate-45">+</span>
              </summary>
              <p className="mt-3 text-slate-600 text-sm leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* -------- HOW IT WORKS / YOUR GLOSSARY -------- */}
      <section className="bg-white border-t border-slate-200 py-16" data-testid="your-glossary-section">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-lg text-slate-700">
            Double-click a word, type what it means once — GlossLink highlights it with <b>your</b> definition on every website and PDF you'll ever open. <b>Never Google it again.</b> Your glossary follows you across the entire internet.
          </p>
          <p className="mt-4 text-lg text-slate-700">
            GlossLink is <b><i>your</i></b> glossary — <b>you decide what every term means in your world</b>. Built for studying, onboarding a new job, or finally speaking your industry's language fluently.
          </p>
          <p className="mt-3 text-sm text-slate-500">
            Works on every webpage and PDF · Nothing sent to a server · Your glossary lives on your computer.
          </p>
        </div>
      </section>

      {/* -------- FINAL CTA -------- */}
      <section className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold mb-4">Stop breaking your reading flow.</h2>
          <p className="text-blue-100 mb-10 text-lg max-w-2xl mx-auto">Install GlossLink and the terms you care about will just… be defined. Everywhere. Forever.</p>

          {/* Research-backed stats */}
          <div className="grid sm:grid-cols-3 gap-4 mb-8 max-w-3xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6" data-testid="cta-stat-0">
              <div className="text-5xl font-black text-white mb-2">2-4×</div>
              <div className="text-sm text-blue-100 leading-snug">Faster vocabulary learning<br/><span className="text-xs text-blue-200/80">(30 min of daily reading)</span></div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6" data-testid="cta-stat-1">
              <div className="text-5xl font-black text-white mb-2">8-12</div>
              <div className="text-sm text-blue-100 leading-snug">Encounters needed to retain a new word<br/><span className="text-xs text-blue-200/80">(Nation, 2001)</span></div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6" data-testid="cta-stat-2">
              <div className="text-5xl font-black text-white mb-2">ONCE</div>
              <div className="text-sm text-blue-100 leading-snug">Just one look-up per word<br/><span className="text-xs text-blue-200/80">— for life</span></div>
            </div>
          </div>

          <p className="text-xs italic text-blue-200 mb-8 max-w-xl mx-auto">
            💡 Every hover is active retrieval — 2× more effective than passive re-reading (Roediger &amp; Karpicke, 2006).
          </p>

          <a href={CWS_LISTING_URL} target="_blank" rel="noopener noreferrer" className="inline-block bg-white text-blue-700 hover:bg-blue-50 px-8 py-4 rounded-xl font-bold text-lg shadow-2xl transition" data-testid="final-cta-btn">
            Add to Chrome — free →
          </a>
          <div className="mt-4 text-sm text-blue-200">Free: 20 words forever · $9.99/mo for unlimited · No account needed</div>
        </div>
      </section>

      {/* -------- FOOTER -------- */}
      <footer className="border-t border-slate-200 mt-16 py-10 bg-white">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-slate-500">
          <div>© {new Date().getFullYear()} GlossLink. Built for readers who don't want to Google every term.</div>
          <div className="flex gap-6">
            <Link to="/for-nurses" className="hover:text-blue-600" data-testid="footer-nurses">For nurses</Link>
            <Link to="/privacy" className="hover:text-blue-600" data-testid="footer-privacy">Privacy</Link>
            <a href="mailto:support@glosslink.app" className="hover:text-blue-600" data-testid="footer-support">Support</a>
          </div>
        </div>
      </footer>

    </div>
  );
};

// -------- Learners Section (language learners, students, curious readers) --------
const LEARNER_PERSONAS = [
  { emoji: "🌐", title: "English learners (ESL)", size: "1.5 billion worldwide", quote: "Look up a word. Forget it. Look it up again. Repeat forever." },
  { emoji: "🎓", title: "Test prep students", size: "SAT · GRE · GMAT · MCAT · TOEFL · IELTS", quote: "Flashcards are boring. Words don't stick." },
  { emoji: "📖", title: "Serious readers", size: "WSJ · NYT · Atlantic · Economist", quote: "Encounter 'hegemony,' vaguely know it, want to own it." },
  { emoji: "📕", title: "Non-fiction & book clubs", size: "50M+ in the US alone", quote: "Every new book = new vocabulary they'll never see again unless they capture it." },
  { emoji: "👨‍👧", title: "Parents & teens", size: "Every household", quote: "Kid doesn't know 'obsequious.' Parents say 'look it up.' Gone by tomorrow." },
  { emoji: "🎯", title: "Career switchers", size: "Millions each year", quote: "Learning crypto, AI, real estate? New terms every paragraph." },
  { emoji: "👴", title: "Older readers", size: "60M+ US adults 55+", quote: "Modern slang and tech terms feel foreign in every article." },
];

const LEARNING_LANGUAGES = {
  english: {
    label: "🇬🇧 Learning English",
    story: 'The politician\'s <mark>rhetoric</mark> was <mark>quintessentially</mark> <mark>partisan</mark>, invoking <mark>hegemony</mark> and <mark>fungibility</mark> in the same breath — a <mark>flabbergasting</mark> display of <mark>obfuscation</mark>. The <mark>pundits</mark> called it <mark>disingenuous</mark>; his supporters called it <mark>bold</mark>.',
    terms: {
      rhetoric: "persuasive speaking or writing (often to influence)",
      quintessentially: "in the most perfect or typical way",
      partisan: "strongly biased toward one political side",
      hegemony: "dominance or leadership by one group over others",
      fungibility: "the quality of being interchangeable (like money or fuel)",
      flabbergasting: "extremely surprising; shocking",
      obfuscation: "deliberately making something unclear or hard to understand",
      pundits: "commentators who give opinions in the media",
      disingenuous: "not truly honest; pretending to be sincere",
      bold: "confident and courageous",
    },
  },
  spanish: {
    label: "🇪🇸 Learning Spanish",
    story: 'Aprovecho esta oportunidad para agradecer su <mark>paciencia</mark>. A pesar del <mark>retraso</mark> con <mark>la avería</mark>, hemos logrado <mark>superar el reto</mark> y <mark>salir adelante</mark>. Espero que <mark>te toque</mark> revisar el <mark>fontanero</mark> antes de que <mark>se agote</mark> el tiempo.',
    terms: {
      paciencia: "patience",
      retraso: "delay",
      "la avería": "the breakdown / malfunction",
      "superar el reto": "overcome the challenge",
      "salir adelante": "get through it; move forward",
      "te toca": "it's your turn (informal)",
      fontanero: "plumber (Spain) / plomero (Latin America)",
      "se agote": "runs out / gets used up",
    },
  },
  mandarin: {
    label: "🇨🇳 Learning Mandarin",
    story: '这个 <mark>软件</mark> 真的很 <mark>方便</mark>，不过我还没 <mark>习惯</mark>。<mark>尽管</mark> 有一些 <mark>困难</mark>，但我 <mark>相信</mark> 我可以 <mark>克服</mark>。学中文需要 <mark>坚持</mark> 和 <mark>耐心</mark>。',
    terms: {
      软件: "ruǎnjiàn — software",
      方便: "fāngbiàn — convenient",
      习惯: "xíguàn — get used to; habit",
      尽管: "jǐnguǎn — even though; despite",
      困难: "kùnnán — difficulty",
      相信: "xiāngxìn — believe / trust",
      克服: "kèfú — overcome",
      坚持: "jiānchí — persist; keep at it",
      耐心: "nàixīn — patience",
    },
  },
  japanese: {
    label: "🇯🇵 Learning Japanese",
    story: '新しい <mark>仕事</mark> に <mark>慣れる</mark> のは <mark>難しい</mark> けど、<mark>先輩</mark> が <mark>親切</mark> に <mark>教えてくれる</mark> から <mark>大丈夫</mark>。<mark>お疲れ様です</mark> と言えば、みんな笑顔になる。',
    terms: {
      仕事: "shigoto — job / work",
      慣れる: "nareru — get used to (something)",
      難しい: "muzukashii — difficult",
      先輩: "senpai — senior colleague / mentor",
      親切: "shinsetsu — kind / helpful",
      教えてくれる: "oshiete-kureru — teaches me (as a favor)",
      大丈夫: "daijōbu — it's okay / no problem",
      "お疲れ様です": "otsukaresama desu — thank you for your hard work (common workplace phrase)",
    },
  },
  french: {
    label: "🇫🇷 Learning French",
    story: 'Il <mark>s\'est débrouillé</mark> pour <mark>économiser</mark> assez d\'argent <mark>malgré</mark> son <mark>loyer</mark> <mark>exorbitant</mark>. Maintenant il <mark>en a marre</mark> de la ville et veut <mark>déménager</mark> à la <mark>campagne</mark>.',
    terms: {
      "s'est débrouillé": "he managed / got by (colloquial)",
      économiser: "to save (money)",
      malgré: "despite / in spite of",
      loyer: "rent (for housing)",
      exorbitant: "extremely high; outrageous",
      "en a marre": "is fed up / sick of it (colloquial)",
      déménager: "to move house",
      campagne: "countryside (also: political campaign, depending on context)",
    },
  },
};

const LearnersSection = () => {
  const [lang, setLang] = useState("english");
  const [showHighlights, setShowHighlights] = useState(true);
  const langData = LEARNING_LANGUAGES[lang];

  const rendered = showHighlights
    ? langData.story.replace(/<mark>(.+?)<\/mark>/g, (_, t) => `<mark class="bg-yellow-200 border-b-2 border-yellow-500 px-1 rounded-sm cursor-help font-semibold" title="${(langData.terms[t] || "").replace(/"/g, "&quot;")}">${t}</mark>`)
    : langData.story.replace(/<\/?mark>/g, "");

  return (
    <section id="learners" className="bg-gradient-to-b from-slate-50 to-white border-y border-slate-200 py-20">
      <div className="max-w-6xl mx-auto px-6">
        {/* Intro */}
        <div className="text-center mb-14">
          <div className="inline-block bg-emerald-100 text-emerald-800 text-xs font-bold px-3 py-1 rounded-full mb-4">🌍 FOR ANYONE WHO READS</div>
          <h2 className="text-3xl sm:text-4xl font-extrabold mb-4 max-w-3xl mx-auto leading-tight">Not just for jobs — a personal dictionary for <span className="text-emerald-700">anyone learning a language</span> or reading hard stuff online.</h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">Add any word or phrase once. See <b>your</b> definition every time it appears — on articles, subtitles, PDFs, everywhere. Never re-learn the same word twice.</p>
        </div>

        {/* Persona grid */}
        <h3 className="text-lg font-bold text-center text-slate-700 mb-6 uppercase tracking-wider">Who's already using it this way</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
          {LEARNER_PERSONAS.map((p, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-lg hover:-translate-y-0.5 transition" data-testid={`persona-${i}`}>
              <div className="text-3xl mb-2">{p.emoji}</div>
              <div className="font-bold text-slate-900 text-base mb-0.5">{p.title}</div>
              <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-2">{p.size}</div>
              <div className="text-sm text-slate-600 italic leading-relaxed">"{p.quote}"</div>
            </div>
          ))}
        </div>

        {/* Language demo */}
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-6">
            <div className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">✨ Try it</div>
            <h3 className="text-2xl font-bold mb-2">Pick a language you're learning</h3>
            <p className="text-slate-600 text-sm">Same story, two views. Toggle to feel the difference.</p>
          </div>

          {/* Language picker */}
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            {Object.entries(LEARNING_LANGUAGES).map(([key, data]) => (
              <button
                key={key}
                onClick={() => setLang(key)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
                  lang === key
                    ? "bg-emerald-600 text-white shadow"
                    : "bg-white text-slate-700 border border-slate-300 hover:border-emerald-500"
                }`}
                data-testid={`lang-tab-${key}`}
              >
                {data.label}
              </button>
            ))}
          </div>

          {/* Before / After toggle */}
          <div className="flex items-center justify-center gap-3 mb-3">
            <button
              onClick={() => setShowHighlights(false)}
              className={`px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${
                !showHighlights ? "bg-slate-900 text-white shadow scale-105" : "bg-white text-slate-600 border-2 border-slate-200 hover:border-slate-400"
              }`}
              data-testid="learners-toggle-before"
            >
              👀 Before
            </button>
            <span className="text-slate-400 text-xs font-medium">vs</span>
            <button
              onClick={() => setShowHighlights(true)}
              className={`px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${
                showHighlights ? "bg-emerald-600 text-white shadow scale-105" : "bg-white text-slate-600 border-2 border-slate-200 hover:border-emerald-400"
              }`}
              data-testid="learners-toggle-after"
            >
              ✨ After (with GlossLink)
            </button>
          </div>
          <p className="text-center text-xs text-slate-500 mb-6 max-w-md mx-auto">
            {showHighlights ? "This is what the page looks like with your personal glossary. Hover any highlighted term." : "This is how a learner sees it — unfamiliar words, no context, no definitions."}
          </p>

          {/* Story */}
          <article
            className={`border rounded-2xl p-8 transition-all ${showHighlights ? "bg-white border-slate-200" : "bg-slate-50 border-slate-300"}`}
            data-testid="learners-story"
          >
            <p
              className={`text-lg leading-relaxed ${showHighlights ? "" : "text-slate-700"}`}
              dangerouslySetInnerHTML={{ __html: rendered }}
            />
          </article>

          <p className="mt-6 text-center text-sm text-slate-500 italic">
            💡 The magic: you add each word <b>once</b>. It lights up on every article, subtitle, and PDF you read — forever.
          </p>
        </div>

        {/* Platform grid — where GlossLink works */}
        <div className="mt-20 max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-sm font-bold text-emerald-700 uppercase tracking-wider mb-3">🔗 Where it works</div>
            <h3 className="text-2xl sm:text-3xl font-extrabold mb-3 max-w-3xl mx-auto">Works alongside every course platform you already use.</h3>
            <p className="text-slate-600 max-w-2xl mx-auto">Every subtitle, transcript, article, and PDF becomes a lesson that never stops teaching. Words you save keep re-appearing highlighted with your definition on everything you read — so you learn faster, remember longer, and never look the same word up twice.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {PLATFORM_BUCKETS.map((b, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-lg hover:-translate-y-0.5 transition" data-testid={`platform-bucket-${i}`}>
                <div className="text-2xl mb-2">{b.emoji}</div>
                <h4 className="font-bold text-slate-900 text-base mb-3">{b.title}</h4>
                <div className="text-sm font-semibold text-slate-700 mb-3 leading-relaxed">
                  {b.platforms.join(" · ")}
                </div>
                <div className="text-xs text-slate-600 italic leading-relaxed">{b.pitch}</div>
              </div>
            ))}
          </div>

          <p className="mt-8 text-center text-sm text-slate-500 max-w-2xl mx-auto">
            No integrations, no accounts, no APIs. GlossLink runs on top of whatever tab you're already reading in.
          </p>
        </div>
      </div>
    </section>
  );
};

const PLATFORM_BUCKETS = [
  { emoji: "🎓", title: "MOOCs & pro courses", platforms: ["Coursera", "edX", "Udemy", "LinkedIn Learning", "MasterClass"], pitch: "Dense lecture jargon defined inline — highlighted right on the transcript, no more pausing to Google." },
  { emoji: "🌍", title: "Language apps", platforms: ["Duolingo", "Babbel", "Rosetta Stone", "iTalki", "HelloTalk"], pitch: "Perfect after each lesson. Add today's new words → see them on real news, articles, and subtitles instantly." },
  { emoji: "🎯", title: "Test prep", platforms: ["Magoosh (GRE/GMAT)", "Kaplan (SAT/MCAT/LSAT)", "Princeton Review", "UWorld (medical)"], pitch: "Turn every article you read into vocab practice. Test-prep terms highlighted wherever you scroll." },
  { emoji: "🧑‍🎓", title: "K-12 & university", platforms: ["Khan Academy", "Google Classroom", "Canvas", "Blackboard", "Moodle"], pitch: "Parents pre-load a kid's vocab. Teachers share class glossaries. Every reading assignment gets easier." },
  { emoji: "💻", title: "Tech & dev learning", platforms: ["Codecademy", "DataCamp", "Pluralsight", "freeCodeCamp"], pitch: "Programming lingo (mutex, ORM, hyperparameter, RAG) defined without breaking your flow." },
  { emoji: "📚", title: "Everywhere else", platforms: ["Wikipedia", "WSJ · NYT · Atlantic · Economist", "YouTube subtitles", "Reddit · Twitter · PDFs"], pitch: "Anywhere a term appears — anywhere you already read. That's where GlossLink lights it up." },
];


const Privacy = () => (
  <div className="min-h-screen bg-slate-50 text-slate-900 font-sans">
    <nav className="sticky top-0 z-50 bg-white/85 backdrop-blur border-b border-slate-200">
      <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-bold text-lg" data-testid="privacy-brand"><span className="text-2xl">📒</span> GlossLink</Link>
        <Link to="/" className="text-sm text-blue-600 hover:underline" data-testid="privacy-back">← Back to home</Link>
      </div>
    </nav>
    <main className="max-w-3xl mx-auto px-6 py-16">
      <h1 className="text-4xl font-extrabold mb-2">Privacy Policy</h1>
      <p className="text-sm text-slate-500 mb-10">Last updated {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>
      <div className="prose prose-slate max-w-none space-y-6">
        <section>
          <h2 className="text-2xl font-bold mt-8 mb-3">Short version</h2>
          <p>GlossLink stores your glossary in your browser (chrome.storage.local) and nowhere else. We do not collect, sell, or share your data. Payment processing is handled by Stripe (via ExtensionPay).</p>
        </section>
        <section>
          <h2 className="text-2xl font-bold mt-8 mb-3">What GlossLink accesses</h2>
          <ul className="list-disc ml-6 space-y-2">
            <li><b>Web page content</b> — locally, only to find and highlight matches from your glossary. Text never leaves your browser.</li>
            <li><b>chrome.storage.local</b> — to save your glossaries, license state, and preferences.</li>
            <li><b>ExtensionPay</b> — for payment processing. See <a className="text-blue-600 underline" href="https://extensionpay.com/privacy" target="_blank" rel="noopener noreferrer">their privacy policy</a>.</li>
          </ul>
        </section>
        <section>
          <h2 className="text-2xl font-bold mt-8 mb-3">What we do NOT do</h2>
          <ul className="list-disc ml-6 space-y-2">
            <li>No analytics, no tracking pixels, no telemetry.</li>
            <li>No sending your glossary or page content to any server.</li>
            <li>No selling data. Ever.</li>
          </ul>
        </section>
        <section>
          <h2 className="text-2xl font-bold mt-8 mb-3">Contact</h2>
          <p>Questions? Email <a href="mailto:support@glosslink.app" className="text-blue-600 underline">support@glosslink.app</a>.</p>
        </section>
      </div>
    </main>
  </div>
);

export { Home, Privacy, HoverTerm, DEMOS };
